import { useQuery } from 'react-query';
import { authFetch } from '../../CommonUtils/authFetch';
import { urlWithParams } from '../../CommonUtils/urlWithParams';
import { PerformancePeriodDTO } from '../types';
import { dateToISO8601 } from '../helpers/dates';
import { useIsMobile } from '../../_hooks/useIsMobile';
import { QueryFeature } from '../../../config/queryClient';

interface ChartDataDTO {
  date: string;
  value: number;
}

interface ValueAmountInvestedDTO {
  name: string;
  data: ChartDataDTO[];
}

interface FetchValueVsAmountInvestedParams {
  clientId: string;
  period?: PerformancePeriodDTO;
  startDate?: string;
  endDate?: string;
  accountId: string;
  groupId: string;
  maxSamples?: number;
}

const fetchValueVsAmountInvested = async ({
  clientId,
  period,
  startDate,
  endDate,
  accountId,
  groupId,
  maxSamples,
}: FetchValueVsAmountInvestedParams): Promise<ValueAmountInvestedDTO[]> =>
  authFetch(
    urlWithParams(
      '/Consumer/OrionAccountsWidgets/ValueVsAmountInvested',
      {
        clientId,
        period,
        startDate,
        endDate,
        accountId: accountId || null,
        groupId: groupId || null,
        localDate: dateToISO8601(),
        maxSamples,
      },
      { skipNulls: true },
    ),
  );

export const VALUE_VS_AMOUNT_INVESTED_QUERY_KEY = 'valueVsAmountInvested';

interface UseValueVsAmountInvestedQueryParams {
  clientId: string;
  period?: PerformancePeriodDTO;
  startDate: string;
  endDate: string;
  accountId: string;
  groupId: string;
}

export const useValueVsAmountInvestedQuery = ({
  clientId,
  period,
  startDate: _startDate,
  endDate: _endDate,
  accountId,
  groupId,
}: UseValueVsAmountInvestedQueryParams) => {
  const startDate = period === undefined ? _startDate : undefined;
  const endDate = period === undefined ? _endDate : undefined;

  const isMobile = useIsMobile();

  // Improve chart performance for mobile devices
  const maxSamples = isMobile ? 30 : 300;

  return useQuery(
    [
      QueryFeature.AccountPerformanceReporting,
      VALUE_VS_AMOUNT_INVESTED_QUERY_KEY,
      clientId,
      period,
      startDate,
      endDate,
      accountId,
      groupId,
      maxSamples,
    ],
    () =>
      fetchValueVsAmountInvested({
        clientId,
        period,
        startDate,
        endDate,
        accountId,
        groupId,
        maxSamples,
      }),
    {
      ...(period === undefined ? { cacheTime: 0 } : {}), // Do not cache custom ranges
    },
  );
};
